/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, Button, TextField, Table, TableBody, TableRow, TableCell, Paper, Typography, TableHead,
  Box, Chip, TableContainer, Alert, Snackbar, CircularProgress, LinearProgress, TableFooter, Pagination, InputAdornment, alpha, useTheme, useMediaQuery,
  IconButton, DialogTitle, DialogContent, DialogActions, Grid
} from '@mui/material';
import { ProdutosService, IListagemDeProdutos, IGetProdutobyid, IDetalhesProdutobyid } from '../../../shared/services/api/estoque/produtos/ProdutosService';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import KeyboardRoundedIcon from '@mui/icons-material/KeyboardRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import PriceChangeRoundedIcon from '@mui/icons-material/PriceChangeRounded';
import { Environment } from '../../../shared/environment';
import { OrdenarPorControl, ListagemBuscaField, IOrdenarPorOpcao } from '../../../shared/components';

interface IFormData {
  iditens: string;
  idprodutos: string;
  qtds: number;
  qtde: number;
  un: string;
  preco: number;
  ean?: string;
  ncm?: string;
  subtotal: number;
  desconto: number;
  voutros: number;
  total: number;
  filtro: string;
  referencia?: string;
  marca?: string;
  descricao: string;
}

export interface ConfirmationDialogRawProps {
  id: string;
  value: string;
  open: boolean;
  status: boolean;
  dados?: IFormData;
  onDados: (dados: IFormData) => void;
  onClose: (value?: string) => void;
}

const tiposBuscaProduto: IOrdenarPorOpcao[] = [
  { nameBD: 'descricao', descricao: 'Descrição' },
  { nameBD: 'referencia', descricao: 'Referência' },
  { nameBD: 'codinterno', descricao: 'Código Interno' },
];

const modosDeBuscaProduto: IOrdenarPorOpcao[] = [
  { nameBD: 'inicio', descricao: 'Início do texto' },
  { nameBD: 'contem', descricao: 'Em qualquer parte do texto' },
];

interface ICachePaginaProdutos {
  pagina: number;
  contexto: string;
  data: IListagemDeProdutos[];
  totalCount: number;
}

// Mesma paleta usada em DialogVenda.tsx (dialog de adicionar produtos da venda) — esta
// dialog foi redesenhada pra ter a mesma cara/comportamento daquela, trocando só a regra de
// preço/quantidade (aqui o valor total do item na NF-e é fixo, ver handleQuantidadeChange/
// handlePrecoChange).
const PALETA = {
  sucesso: '#16A34A',
  atencao: '#F59E0B',
};

const CardEstatistica: React.FC<{ icon: React.ReactNode; label: string; valor: React.ReactNode; corValor?: string }> = ({ icon, label, valor, corValor }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.75, sm: 1 },
        px: { xs: 1.25, sm: 1.75 },
        py: { xs: 0.75, sm: 1 },
        borderRadius: '12px',
        border: `1px solid ${theme.torq.border.default}`,
        backgroundColor: theme.torq.surface.card,
        minWidth: { xs: 108, sm: 140 },
      }}
    >
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: { xs: '0.62rem', sm: '0.68rem' }, color: 'text.secondary', lineHeight: 1.3 }}>
          {label}
        </Typography>
        <Typography noWrap sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 700, color: corValor ?? theme.torq.text.primary, lineHeight: 1.3 }}>
          {valor}
        </Typography>
      </Box>
    </Box>
  );
};

export const DialogNfeEntrada: React.FC<ConfirmationDialogRawProps> = (props) => {
  const { onClose, open, dados } = props;
  const theme = useTheme();
  const larguraPequena = useMediaQuery(theme.breakpoints.down('md'));
  const alturaPequena = useMediaQuery('(max-height:700px)');
  const telaPequena = larguraPequena || alturaPequena;

  const requestIdRef = useRef(0);
  const focoNaTrocaDePaginaRef = useRef<'primeiro' | 'ultimo' | null>(null);
  const [produtos, setProdutos] = useState<IListagemDeProdutos[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filtro, setFiltro] = useState('');
  const [tipoBusca, setTipoBusca] = useState('descricao');
  const [modoBusca, setModoBusca] = useState('inicio');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const cacheAdjacenteRef = useRef<{ anterior: ICachePaginaProdutos | null; seguinte: ICachePaginaProdutos | null }>({
    anterior: null,
    seguinte: null,
  });
  const contextoBusca = useMemo(
    () => `${tipoBusca}|${filtro}|${modoBusca}|${rowsPerPage}`,
    [tipoBusca, filtro, modoBusca, rowsPerPage],
  );
  const [isLoading, setIsLoading] = useState(false);

  // Produto do catálogo vinculado a este item da NF-e — usado só pra destacar a linha na
  // tabela e mostrar os detalhes no painel inferior. Os campos que efetivamente vão pro
  // formulário (idprodd, descricao, marca, referencia, un) são estados à parte porque, ao
  // editar um item que já tem produto vinculado, esses valores vêm de `dados` (a linha da
  // nota) e não necessariamente de uma seleção na tabela.
  const [produtoSelecionado, setProdutoSelecionado] = useState<IListagemDeProdutos | null>(null);
  const [idprodd, setIdprodd] = useState('');
  const [descricao, setDescricao] = useState('');
  const [referencia, setReferencia] = useState<string | undefined>('');
  const [marca, setMarca] = useState<string | undefined>('');
  const [un, setUn] = useState('');
  const [erroUnidade, setErroUnidade] = useState('');
  // Nome do item truncado no cabeçalho (ver Typography no header) — clicar nele alterna pra
  // mostrar o nome inteiro, quebrando linha, quando ele é grande demais pra caber de primeira.
  const [nomeItemExpandido, setNomeItemExpandido] = useState(false);

  // Regra de negócio desta dialog (igual à que já existia): o valor total do item na NF-e
  // (totalNotaOriginal) é fixo. Alterar a quantidade recalcula o valor unitário pra manter
  // esse total; alterar o valor unitário recalcula a quantidade — nunca o contrário.
  // `quantidade`/`precoUnitario` são os valores ARREDONDADOS (casasDecimais) exibidos nos
  // campos; `quantidadeInterna`/`precoUnitarioInterno` guardam a mesma coisa em precisão
  // cheia (sem arredondar) e são o que efetivamente entra nas contas — sem essa cópia exata,
  // arredondar a quantidade pra 2 casas antes de multiplicar pelo preço podia derrubar o
  // total alguns centavos fora da tolerância e disparar "Inconsistência nos valores" mesmo
  // com os campos preenchidos corretamente.
  const [quantidade, setQuantidade] = useState(0);
  const [quantidadeInterna, setQuantidadeInterna] = useState(0);
  const [precoUnitario, setPrecoUnitario] = useState(0);
  const [precoUnitarioInterno, setPrecoUnitarioInterno] = useState(0);
  const [totalNotaOriginal, setTotalNotaOriginal] = useState(0);
  const [casasDecimais, setCasasDecimais] = useState(2);

  // Edição da formação de preço do produto selecionado (mesmos campos/fórmulas da aba
  // "Formando Preço" do cadastro de produto, ver DialogCadastrarProd.tsx) — botão à parte,
  // separado de "Vincular", pra atualizar o CADASTRO do produto (preço de compra, percentuais
  // de custo, preço de custo calculado, margem e preço de venda) sem alterar o vínculo/valores
  // deste item da NF-e.
  const [precoFormOpen, setPrecoFormOpen] = useState(false);
  const [precoFormLoading, setPrecoFormLoading] = useState(false);
  const [produtoCompleto, setProdutoCompleto] = useState<IGetProdutobyid | null>(null);
  // As duas referências de preço de compra entre as quais o botão de alternar troca — o preço
  // já cadastrado hoje no produto (precoAtualProduto) e o valor unitário desta compra/NF-e
  // (precoNovaCompra). `origemPreco` só controla qual das duas está em uso; o valor efetivo
  // do campo "Preço De Compra" continua em precoForm.preco (editável manualmente também).
  const [precoAtualProduto, setPrecoAtualProduto] = useState(0);
  const [precoNovaCompra, setPrecoNovaCompra] = useState(0);
  // 'manual' quando o campo "Preço De Compra" foi editado direto (nem um botão nem outro
  // representa mais o valor exibido) — só usado pra decidir qual botão aparece "ativo".
  const [origemPreco, setOrigemPreco] = useState<'atual' | 'novaCompra' | 'manual'>('novaCompra');
  const [precoForm, setPrecoForm] = useState({
    preco: 0,
    comissao: 0,
    icms: 0,
    pseguro: 0,
    pfrete: 0,
    desconto: 0,
    ppromocao: 0,
    precocusto: 0,
    margem: 0,
    pvenda: 0,
  });

  const qtdInputRef = useRef<HTMLInputElement>(null);
  const precoInputRef = useRef<HTMLInputElement>(null);
  const buscaRef = useRef<HTMLInputElement>(null);
  const tabelaRef = useRef<HTMLDivElement>(null);
  const footerRowRef = useRef<HTMLTableRowElement>(null);
  const btnVincularRef = useRef<HTMLButtonElement>(null);

  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const ultimaAlturaMedidaRef = useRef<number>(0);
  const alturaLinhaMedidaRef = useRef<number | null>(null);
  const alturaFooterMedidaRef = useRef<number | null>(null);

  const ALTURA_LINHA_PADRAO = 30;
  const FOOTER_RESERVADO_PADRAO = 56;

  const calcularLimiteDinamico = (alturaContainer: number) => {
    const container = tabelaRef.current;
    if (!container) {
      return;
    }

    const FOOTER_RESERVADO = alturaFooterMedidaRef.current ?? FOOTER_RESERVADO_PADRAO;
    const headerEl = container.querySelector('thead');
    const alturaHeader = headerEl ? Math.round(headerEl.getBoundingClientRect().height) : 34;
    const alturaLinha = alturaLinhaMedidaRef.current ?? ALTURA_LINHA_PADRAO;
    const alturaAtual = Math.round(alturaContainer);

    if (Math.abs(alturaAtual - ultimaAlturaMedidaRef.current) < 2) {
      return;
    }
    ultimaAlturaMedidaRef.current = alturaAtual;

    const MIN_ROWS = 4;
    const MAX_ROWS = 50;
    const FOLGA_LINHAS = (alturaLinhaMedidaRef.current !== null && alturaFooterMedidaRef.current !== null) ? 0.5 : 1;
    const alturaDisponivel = alturaAtual - alturaHeader - FOOTER_RESERVADO;

    if (alturaDisponivel <= 0) {
      return;
    }

    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / alturaLinha - FOLGA_LINHAS))
    );

    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  useLayoutEffect(() => {
    if (!open) return;

    ultimaAlturaMedidaRef.current = 0;

    const container = tabelaRef.current;
    if (!container) return;

    calcularLimiteDinamico(container.getBoundingClientRect().height);

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        calcularLimiteDinamico(entry.contentRect.height);
      }
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [open]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  useEffect(() => {
    if (!open) return;

    const cacheAtual = cacheAdjacenteRef.current;
    const cache = cacheAtual.anterior?.pagina === page && cacheAtual.anterior?.contexto === contextoBusca
      ? cacheAtual.anterior
      : cacheAtual.seguinte?.pagina === page && cacheAtual.seguinte?.contexto === contextoBusca
        ? cacheAtual.seguinte
        : null;
    if (cache) {
      cacheAdjacenteRef.current = { anterior: null, seguinte: null };
      ++requestIdRef.current;
      setIsLoading(false);
      setProdutos(cache.data);
      setTotalCount(cache.totalCount);
      if (focoNaTrocaDePaginaRef.current === 'ultimo') {
        setSelectedIndex(Math.max(cache.data.length - 1, 0));
      } else if (focoNaTrocaDePaginaRef.current === 'primeiro') {
        setSelectedIndex(0);
      }
      focoNaTrocaDePaginaRef.current = null;
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setProdutos([]);
    setTotalCount(0);

    if (!focoNaTrocaDePaginaRef.current) {
      setSelectedIndex(0);
    }

    ProdutosService.getAll(page + 1, filtro, tipoBusca, rowsPerPage, modoBusca).then(res => {
      if (requestId !== requestIdRef.current) return;
      setIsLoading(false);

      if (!(res instanceof Error)) {
        setProdutos(res.data);
        setTotalCount(res.totalCount || 0);

        if (focoNaTrocaDePaginaRef.current === 'ultimo') {
          setSelectedIndex(Math.max(res.data.length - 1, 0));
        } else if (focoNaTrocaDePaginaRef.current === 'primeiro') {
          setSelectedIndex(0);
        }
        focoNaTrocaDePaginaRef.current = null;
      } else {
        focoNaTrocaDePaginaRef.current = null;
        setProdutos([]);
        setTotalCount(0);
        setMsgType('error');
        setMsg(res.message);
        setOpenMsg(true);
      }
    });

  }, [filtro, tipoBusca, modoBusca, open, page, rowsPerPage, contextoBusca]);

  useEffect(() => {
    if (!open || isLoading || produtos.length === 0) return;
    const totalPaginasAtual = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    const alvoSeguinte = page + 1 < totalPaginasAtual ? page + 1 : null;
    const alvoAnterior = page > 0 ? page - 1 : null;
    const cacheAtual = cacheAdjacenteRef.current;
    let cancelado = false;

    if (alvoSeguinte !== null && !(cacheAtual.seguinte?.pagina === alvoSeguinte && cacheAtual.seguinte?.contexto === contextoBusca)) {
      ProdutosService.getAll(alvoSeguinte + 1, filtro, tipoBusca, rowsPerPage, modoBusca).then((result) => {
        if (cancelado || result instanceof Error || !result.data || result.data.length === 0) return;
        cacheAdjacenteRef.current = {
          ...cacheAdjacenteRef.current,
          seguinte: { pagina: alvoSeguinte, contexto: contextoBusca, data: result.data, totalCount: result.totalCount },
        };
      });
    }
    if (alvoAnterior !== null && !(cacheAtual.anterior?.pagina === alvoAnterior && cacheAtual.anterior?.contexto === contextoBusca)) {
      ProdutosService.getAll(alvoAnterior + 1, filtro, tipoBusca, rowsPerPage, modoBusca).then((result) => {
        if (cancelado || result instanceof Error || !result.data || result.data.length === 0) return;
        cacheAdjacenteRef.current = {
          ...cacheAdjacenteRef.current,
          anterior: { pagina: alvoAnterior, contexto: contextoBusca, data: result.data, totalCount: result.totalCount },
        };
      });
    }

    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalCount, isLoading, contextoBusca, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const container = tabelaRef.current;
    if (!container) return;

    let mudou = false;

    const linhaEl = container.querySelector('tbody tr[data-row-index]');
    if (linhaEl) {
      const alturaLinha = Math.round(linhaEl.getBoundingClientRect().height);
      if (alturaLinha > 0 && alturaLinhaMedidaRef.current !== alturaLinha) {
        alturaLinhaMedidaRef.current = alturaLinha;
        mudou = true;
      }
    }

    const footerEl = footerRowRef.current;
    if (footerEl) {
      const alturaFooter = Math.round(footerEl.getBoundingClientRect().height);
      if (alturaFooter > 0 && alturaFooterMedidaRef.current !== alturaFooter) {
        alturaFooterMedidaRef.current = alturaFooter;
        mudou = true;
      }
    }

    if (mudou) {
      ultimaAlturaMedidaRef.current = -1;
      calcularLimiteDinamico(container.getBoundingClientRect().height);
    }
  }, [produtos, totalCount, rowsPerPage, open]);

  const formatNumber = (
    value: number | null | undefined,
    options?: Intl.NumberFormatOptions
  ) => {
    return Number(value ?? 0).toLocaleString('pt-BR', options);
  };

  const formatMoney = (value: number) => formatNumber(value, {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: casasDecimais,
    maximumFractionDigits: casasDecimais,
  });

  // Detecta se o total original do item veio com 4 casas decimais (comum em combustíveis) —
  // mantém a mesma lógica que já existia nesta dialog.
  const detectarCasasDecimais = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined || value === '') return 2;
    const str = typeof value === 'number' ? value.toString() : value;
    const match = str.match(/(?:[.,](\d+))/);
    if (match && match[1]) {
      return match[1].length === 4 ? 4 : 2;
    }
    return 2;
  };

  const formatarParaExibicao = (value: number, casas: number): number => {
    const fator = casas === 4 ? 10000 : 100;
    return Math.round(value * fator) / fator;
  };

  const calcularTotalExato = useCallback((qtd: number, preco: number): number => qtd * preco, []);
  const calcularPrecoExato = useCallback((qtd: number, totalNota: number): number => (qtd === 0 ? 0 : totalNota / qtd), []);
  const calcularQuantidadeExata = useCallback((preco: number, totalNota: number): number => (preco === 0 ? 0 : totalNota / preco), []);

  useEffect(() => {
    if (!open) {
      setProdutoSelecionado(null);
      setIdprodd('');
      setDescricao('');
      setReferencia('');
      setMarca('');
      setUn('');
      setErroUnidade('');
      setQuantidade(0);
      setQuantidadeInterna(0);
      setPrecoUnitario(0);
      setPrecoUnitarioInterno(0);
      setTotalNotaOriginal(0);
      setCasasDecimais(2);
      setSelectedIndex(0);
      setFiltro('');
      setTipoBusca('descricao');
      setModoBusca('inicio');
      setPage(0);
      setNomeItemExpandido(false);
      setPrecoFormOpen(false);
      setProdutoCompleto(null);
      setPrecoAtualProduto(0);
      setPrecoNovaCompra(0);
      setOrigemPreco('novaCompra');
      return;
    }

    setNomeItemExpandido(false);
    setTimeout(() => buscaRef.current?.focus(), 100);

    if (dados && typeof dados === 'object') {
      const quantidadeOriginal = dados.qtde || 0;
      const totalOriginal = dados.subtotal || 0;
      const unidadeOriginal = dados.un || '';

      const casasDetectadas = detectarCasasDecimais(totalOriginal);
      setCasasDecimais(casasDetectadas);

      setIdprodd(dados.idprodutos || '');
      setDescricao(dados.descricao || '');
      setReferencia(dados.referencia || '');
      setMarca(dados.marca || '');
      setUn(unidadeOriginal);
      setTotalNotaOriginal(totalOriginal);
      setErroUnidade(!unidadeOriginal || unidadeOriginal.trim() === '' ? 'Unidade é obrigatória' : '');

      const precoExato = quantidadeOriginal > 0 ? totalOriginal / quantidadeOriginal : (dados.preco || 0);
      const precoExibicao = formatarParaExibicao(precoExato, casasDetectadas);

      setQuantidade(quantidadeOriginal);
      setQuantidadeInterna(quantidadeOriginal);
      setPrecoUnitarioInterno(precoExato);
      setPrecoUnitario(precoExibicao);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dados]);

  useEffect(() => {
    if (tabelaRef.current && selectedIndex >= 0) {
      const selectedRow = tabelaRef.current.querySelector(`[data-row-index="${selectedIndex}"]`);
      if (selectedRow) {
        selectedRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F8') {
        event.preventDefault();
        const tipos = ['descricao', 'referencia', 'codinterno'];
        const currentIndex = tipos.indexOf(tipoBusca);
        const nextIndex = (currentIndex + 1) % tipos.length;
        setTipoBusca(tipos[nextIndex]);
        setFiltro('');
        setSelectedIndex(0);
        setPage(0);
        setTimeout(() => buscaRef.current?.focus(), 100);
      }

      if (event.key === 'F2') {
        event.preventDefault();
        qtdInputRef.current?.focus();
        qtdInputRef.current?.select();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleGlobalKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [open, tipoBusca]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (produtos.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (selectedIndex < produtos.length - 1) {
          setSelectedIndex(prev => prev + 1);
        } else if (page + 1 < Math.ceil(totalCount / rowsPerPage)) {
          focoNaTrocaDePaginaRef.current = 'primeiro';
          setPage(prev => prev + 1);
        }
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (selectedIndex > 0) {
          setSelectedIndex(prev => prev - 1);
        } else if (page > 0) {
          focoNaTrocaDePaginaRef.current = 'ultimo';
          setPage(prev => prev - 1);
        }
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const prod = produtos[selectedIndex];
        if (prod) {
          selecionarProduto(prod, selectedIndex);
        }
      }
    }
    if (event.key === 'Escape') {
      onClose();
    }
  };

  // Vincula o produto escolhido a este item da NF-e — NÃO mexe em quantidade/preço/total:
  // esses continuam regidos pelo valor total original do item na nota (totalNotaOriginal),
  // independente de qual produto do catálogo for selecionado.
  const selecionarProduto = (prod: IListagemDeProdutos, index: number) => {
    setSelectedIndex(index);
    setProdutoSelecionado(prod);
    setIdprodd(prod.idproduto);
    setDescricao(prod.descricao);
    setMarca(prod.marca);
    setReferencia(prod.referencia);
    setUn(prod.un);
    setErroUnidade(prod.un && prod.un.trim() !== '' ? '' : 'Unidade é obrigatória');

    setSelectedIndex(-1);

    buscaRef.current?.blur();

    setTimeout(() => {
      if (qtdInputRef.current) {
        qtdInputRef.current.focus();
        qtdInputRef.current.select();
      }
    }, 100);
  };

  // Alterar a quantidade recalcula o valor unitário pra manter o total do item na nota fixo.
  // A quantidade digitada já é exata (não precisa arredondar); quem precisa da cópia exata
  // é o preço recalculado, guardada em precoUnitarioInterno (precoUnitario, arredondado, é só
  // o que aparece no campo).
  const handleQuantidadeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numbers = event.target.value.replace(/\D/g, '');
    const novaQuantidade = numbers ? parseInt(numbers, 10) / 100 : 0;
    setQuantidade(novaQuantidade);
    setQuantidadeInterna(novaQuantidade);

    if (totalNotaOriginal > 0 && novaQuantidade > 0) {
      const novoPrecoExato = calcularPrecoExato(novaQuantidade, totalNotaOriginal);
      const novoPrecoExibicao = formatarParaExibicao(novoPrecoExato, casasDecimais);
      setPrecoUnitarioInterno(novoPrecoExato);
      setPrecoUnitario(novoPrecoExibicao);
    }
  };

  // Alterar o valor unitário recalcula a quantidade pra manter o total do item na nota fixo.
  // A quantidade recalculada aqui É a que precisa da cópia exata (quantidadeInterna): guardar
  // só a versão arredondada (casasDecimais) fazia `quantidade × precoUnitarioInterno` cair
  // alguns centavos fora do total original e disparar "Inconsistência nos valores" mesmo com
  // os campos certos.
  const handlePrecoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numbers = event.target.value.replace(/\D/g, '');
    const novoPreco = numbers ? parseInt(numbers, 10) / 100 : 0;
    setPrecoUnitario(novoPreco);
    setPrecoUnitarioInterno(novoPreco);

    if (totalNotaOriginal > 0 && novoPreco > 0) {
      const novaQuantidadeExata = calcularQuantidadeExata(novoPreco, totalNotaOriginal);
      const novaQuantidadeExibicao = formatarParaExibicao(novaQuantidadeExata, casasDecimais);
      setQuantidadeInterna(novaQuantidadeExata);
      setQuantidade(novaQuantidadeExibicao);
    }
  };

  const handleUnidadeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const novaUnidade = event.target.value;
    setUn(novaUnidade);
    setErroUnidade(!novaUnidade || novaUnidade.trim() === '' ? 'Unidade é obrigatória' : '');
  };

  // Mesma fórmula da aba "Formando Preço" do cadastro de produto (calcularPrecoCusto em
  // DialogCadastrarProd.tsx/DetalheDeProduto.tsx): preço de compra + comissão/ICMS/frete como
  // percentual do preço de compra, seguro já em reais.
  const calcularCustoFormacao = (dados: { preco: number; comissao: number; icms: number; pfrete: number; pseguro: number }): number => {
    return (
      dados.preco +
      dados.preco * (dados.comissao / 100) +
      dados.preco * (dados.icms / 100) +
      dados.preco * (dados.pfrete / 100) +
      dados.pseguro
    );
  };

  const arredondarMoeda = (valor: number): number => Math.round(valor * 100) / 100;

  const parseDigitosParaNumero = (raw: string): number => {
    const numbers = raw.replace(/\D/g, '');
    return numbers ? parseInt(numbers, 10) / 100 : 0;
  };

  // Abre o formulário de preço do produto SELECIONADO na tabela (idprodd) — busca o cadastro
  // completo via getById porque updateById exige o registro inteiro (não só os campos de
  // preço), ver ProdutosService.updateById.
  const abrirEdicaoPreco = () => {
    if (!idprodd) return;
    setPrecoFormOpen(true);
    setPrecoFormLoading(true);
    ProdutosService.getById(idprodd).then((result) => {
      setPrecoFormLoading(false);
      if (result instanceof Error) {
        setMsgType('error');
        setMsg(result.message);
        setOpenMsg(true);
        setPrecoFormOpen(false);
        return;
      }
      setProdutoCompleto(result);

      // As duas referências que o botão de alternar troca: o preço já cadastrado hoje no
      // produto e o valor unitário desta compra/NF-e. Começa usando o desta compra (motivo
      // de existir esta dialog), caindo pro cadastrado só se o item ainda não tiver um valor
      // unitário definido.
      const precoAtual = result.preco ?? 0;
      const precoNovo = precoUnitario;
      setPrecoAtualProduto(precoAtual);
      setPrecoNovaCompra(precoNovo);
      const origemInicial: 'atual' | 'novaCompra' = precoNovo > 0 ? 'novaCompra' : 'atual';
      setOrigemPreco(origemInicial);

      const base = {
        preco: origemInicial === 'novaCompra' ? precoNovo : precoAtual,
        comissao: result.comissao ?? 0,
        icms: result.icms ?? 0,
        pseguro: result.pseguro ?? 0,
        pfrete: result.pfrete ?? 0,
      };
      const margem = result.margem ?? 0;
      const custo = arredondarMoeda(calcularCustoFormacao(base));
      const pvenda = custo > 0 && margem ? arredondarMoeda(custo + custo * (margem / 100)) : custo;

      // Preço promoção acompanha o preço de CUSTO (não o de compra): nunca pode ficar abaixo
      // dele — se o valor cadastrado for menor que o novo custo, sobe junto.
      const ppromocao = Math.max(result.ppromocao ?? 0, custo);

      setPrecoForm({
        ...base,
        desconto: result.desconto ?? 0,
        ppromocao,
        precocusto: custo,
        margem,
        pvenda,
      });
    });
  };

  const fecharEdicaoPreco = () => {
    setPrecoFormOpen(false);
    setProdutoCompleto(null);
  };

  // Botão de alternar: troca o preço de compra entre o valor atualmente cadastrado no produto
  // e o valor unitário desta compra/NF-e — o resto (custo, venda, promoção) recalcula em cima
  // da troca, igual a editar o campo "Preço De Compra" na mão.
  const alternarOrigemPreco = (origem: 'atual' | 'novaCompra') => {
    setOrigemPreco(origem);
    setPrecoForm(prev => {
      const novoPreco = origem === 'atual' ? precoAtualProduto : precoNovaCompra;
      const atualizado = { ...prev, preco: novoPreco };
      const custo = arredondarMoeda(calcularCustoFormacao(atualizado));
      const pvenda = custo > 0 && atualizado.margem
        ? arredondarMoeda(custo + custo * (atualizado.margem / 100))
        : custo;
      const ppromocao = Math.max(atualizado.ppromocao, custo);
      return { ...atualizado, precocusto: custo, pvenda, ppromocao };
    });
  };

  // Alterar preço de compra, comissão, ICMS, frete ou seguro recalcula o preço de custo — e,
  // se já houver uma margem definida, o preço de venda também é recalculado em cima do novo
  // custo (mesmo comportamento de handlePrecoCustoChange em DialogCadastrarProd.tsx). Preço
  // promoção acompanha o preço de CUSTO: se o novo custo ficar acima da promoção atual, ela
  // sobe junto (nunca pode ficar menor que o preço de custo).
  const handlePrecoFormCampoChange = (campo: 'preco' | 'comissao' | 'icms' | 'pseguro' | 'pfrete') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const valor = parseDigitosParaNumero(event.target.value);
    if (campo === 'preco') setOrigemPreco('manual');
    setPrecoForm(prev => {
      const atualizado = { ...prev, [campo]: valor };
      const custo = arredondarMoeda(calcularCustoFormacao(atualizado));
      const pvenda = custo > 0 && atualizado.margem
        ? arredondarMoeda(custo + custo * (atualizado.margem / 100))
        : custo;
      const ppromocao = Math.max(atualizado.ppromocao, custo);
      return { ...atualizado, precocusto: custo, pvenda, ppromocao };
    });
  };

  const handleDescontoFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valor = parseDigitosParaNumero(event.target.value);
    setPrecoForm(prev => ({ ...prev, desconto: valor }));
  };

  // Nunca deixa a promoção ficar abaixo do preço de CUSTO — digitar um valor menor é travado
  // no próprio preço de custo.
  const handlePromocaoFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valor = parseDigitosParaNumero(event.target.value);
    setPrecoForm(prev => ({ ...prev, ppromocao: Math.max(valor, prev.precocusto) }));
  };

  // Alterar a margem recalcula o preço de venda em cima do preço de custo já calculado —
  // nunca mexe no custo (mesmo comportamento de handleMargemChange em DialogCadastrarProd.tsx).
  const handleMargemFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const margem = parseDigitosParaNumero(event.target.value);
    setPrecoForm(prev => {
      if (prev.precocusto <= 0) return { ...prev, margem };
      const pvenda = arredondarMoeda(prev.precocusto + prev.precocusto * (margem / 100));
      return { ...prev, margem, pvenda };
    });
  };

  // Alterar o preço de venda recalcula a margem em cima do preço de custo já calculado —
  // nunca mexe no custo (mesmo comportamento de handlePrecoVendaChange em DialogCadastrarProd.tsx).
  const handlePrecoVendaFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pvenda = parseDigitosParaNumero(event.target.value);
    setPrecoForm(prev => {
      if (prev.precocusto <= 0) return { ...prev, pvenda };
      const margem = Number((((pvenda - prev.precocusto) / prev.precocusto) * 100).toFixed(4));
      return { ...prev, pvenda, margem };
    });
  };

  const salvarPrecoProduto = () => {
    if (!idprodd || !produtoCompleto) return;

    setPrecoFormLoading(true);
    const { quan, ...produtoBase } = produtoCompleto;
    const payload: IDetalhesProdutobyid = {
      ...produtoBase,
      // getById pode trazer esses dois como null (produto nunca teve dados de combustível
      // preenchidos) — o backend rejeita null no update ("pglp/qtdglp cannot be null"), então
      // manda do mesmo jeito que já vinham quando não são null, e 0 só nesse caso.
      pglp: produtoBase.pglp ?? 0,
      qtdglp: produtoBase.qtdglp ?? 0,
      preco: precoForm.preco,
      comissao: precoForm.comissao,
      icms: precoForm.icms,
      pseguro: precoForm.pseguro,
      pfrete: precoForm.pfrete,
      desconto: precoForm.desconto,
      ppromocao: precoForm.ppromocao,
      precocusto: precoForm.precocusto,
      margem: precoForm.margem,
      pvenda: precoForm.pvenda,
    };

    ProdutosService.updateById(idprodd, payload).then((result) => {
      setPrecoFormLoading(false);
      if (result instanceof Error) {
        setMsgType('error');
        setMsg(result.message);
        setOpenMsg(true);
        return;
      }

      setMsgType('success');
      setMsg('Preço do produto atualizado com sucesso!');
      setOpenMsg(true);
      setPrecoFormOpen(false);
      setProdutoCompleto(null);

      // Reflete o novo preço/preço de venda na linha da tabela e no painel do produto
      // selecionado, sem precisar refazer a busca.
      setProdutos(prev => prev.map(p => (p.idproduto === idprodd ? { ...p, preco: precoForm.preco, pvenda: precoForm.pvenda } : p)));
      setProdutoSelecionado(prev => (prev && prev.idproduto === idprodd ? { ...prev, preco: precoForm.preco, pvenda: precoForm.pvenda } : prev));
    });
  };

  const totalExibido = totalNotaOriginal > 0
    ? totalNotaOriginal
    : calcularTotalExato(quantidadeInterna || quantidade, precoUnitarioInterno || precoUnitario);

  const vincularProduto = () => {
    if (!idprodd) {
      setMsgType('warning');
      setMsg('Selecione um produto para vincular a este item da nota!');
      setOpenMsg(true);
      return;
    }

    if (quantidade <= 0) {
      setMsgType('warning');
      setMsg('A quantidade deve ser maior que zero!');
      setOpenMsg(true);
      setTimeout(() => {
        qtdInputRef.current?.focus();
        qtdInputRef.current?.select();
      }, 100);
      return;
    }

    if (precoUnitario <= 0) {
      setMsgType('warning');
      setMsg('O valor unitário deve ser maior que zero!');
      setOpenMsg(true);
      setTimeout(() => {
        precoInputRef.current?.focus();
        precoInputRef.current?.select();
      }, 100);
      return;
    }

    if (!un || un.trim() === '') {
      setErroUnidade('Unidade é obrigatória. Selecione uma unidade para o produto.');
      setMsgType('error');
      setMsg('Unidade é obrigatória. Selecione uma unidade para o produto.');
      setOpenMsg(true);
      return;
    }

    // Rede de segurança: quantidade × preço sempre devem bater com o total original do item
    // na nota — na prática nunca deveria falhar, já que handleQuantidadeChange/
    // handlePrecoChange recalculam um em função do outro em tempo real. Usa as cópias exatas
    // (quantidadeInterna/precoUnitarioInterno), não os valores já arredondados pra exibição —
    // arredondar antes de multiplicar é o que fazia essa checagem estourar a tolerância à toa.
    if (totalNotaOriginal > 0) {
      const totalVerificacao = calcularTotalExato(quantidadeInterna || quantidade, precoUnitarioInterno || precoUnitario);
      const tolerancia = casasDecimais === 4 ? 0.0001 : 0.0099;
      if (Math.abs(totalVerificacao - totalNotaOriginal) > tolerancia) {
        setMsgType('warning');
        setMsg(
          `Inconsistência nos valores: ${formatNumber(quantidade)} × ${formatMoney(precoUnitario)} não bate com o total do item na nota (${formatMoney(totalNotaOriginal)}).`
        );
        setOpenMsg(true);
        return;
      }
    }

    const payload: IFormData = {
      iditens: dados?.iditens ?? '',
      idprodutos: idprodd,
      qtde: quantidadeInterna || quantidade,
      preco: precoUnitario,
      subtotal: totalNotaOriginal > 0 ? totalNotaOriginal : calcularTotalExato(quantidadeInterna || quantidade, precoUnitario),
      un,
      ean: dados?.ean,
      ncm: dados?.ncm,
      descricao: descricao ?? '',
      marca: marca ?? '',
      referencia: referencia ?? '',
      desconto: 0,
      voutros: 0,
      total: 0,
      qtds: 0,
      filtro: 'descricao',
    };

    props.onDados(payload);
    onClose();
  };

  const produtoEmFoco = selectedIndex >= 0 ? produtos[selectedIndex] : produtoSelecionado;
  const isEditing = !!dados?.idprodutos;

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      fullWidth
      maxWidth="xl"
      fullScreen={telaPequena}
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          borderRadius: telaPequena ? 0 : 2,
          overflow: 'hidden',
          ...(telaPequena ? {} : {
            width: '95%',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: '95vh',
            minHeight: '420px',
          }),
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openMsg}
        autoHideDuration={msgType === 'error' ? null : 6000}
        onClose={() => setOpenMsg(false)}
      >
        <Alert onClose={() => setOpenMsg(false)} severity={msgType} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>

      {/* CABEÇALHO — fechar (X/ESC) + título à esquerda, cards de estatística à direita (total
          fixo do item na nota + teclas de atalho). */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 2 },
          px: { xs: 1.25, sm: 1.75, md: 2.5 },
          py: { xs: 1, sm: 1.5, md: 2 },
          borderBottom: `1px solid ${theme.torq.border.default}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
            <IconButton
              onClick={() => onClose()}
              size="small"
              sx={{ border: `1px solid ${theme.torq.border.default}`, borderRadius: '10px', width: { xs: 30, sm: 34 }, height: { xs: 30, sm: 34 } }}
            >
              <CloseIcon fontSize="small" sx={{ color: theme.torq.text.primary }} />
            </IconButton>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 600 }}>ESC</Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem' }, fontWeight: 700, color: theme.palette.primary.main, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3 }}>
              {isEditing ? 'Editar Vínculo do Item da NF-e' : 'Vincular Produto ao Item da NF-e'}
            </Typography>
            {/* Nome do item exatamente como veio na nota (dados.descricao) — não o produto do
                catálogo eventualmente selecionado na tabela abaixo, que pode mudar. Clicável:
                quando trunca (nome grande demais pra caber de primeira), um clique expande pra
                mostrar o nome inteiro quebrando linha; outro clique volta a truncar. */}
            <Typography
              onClick={() => setNomeItemExpandido(prev => !prev)}
              noWrap={!nomeItemExpandido}
              title={nomeItemExpandido ? undefined : (dados?.descricao || undefined)}
              sx={{
                fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
                fontWeight: 800,
                color: theme.torq.text.primary,
                lineHeight: 1.25,
                maxWidth: { xs: 220, sm: 340, md: 480 },
                cursor: 'pointer',
                whiteSpace: nomeItemExpandido ? 'normal' : 'nowrap',
                wordBreak: nomeItemExpandido ? 'break-word' : 'normal',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {dados?.descricao || 'Item da NF-e'}
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' }, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
              Selecione um produto e pressione ENTER
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1.25 } }}>
          <CardEstatistica
            icon={<PaidRoundedIcon sx={{ color: PALETA.sucesso, fontSize: { xs: 20, sm: 26 } }} />}
            label="Total do item (NF-e)"
            valor={formatMoney(totalNotaOriginal)}
            corValor={PALETA.sucesso}
          />
          <CardEstatistica
            icon={<KeyboardRoundedIcon sx={{ color: theme.torq.text.primary, fontSize: { xs: 20, sm: 26 } }} />}
            label="Teclas de atalho"
            valor="F8 • ENTER • ESC"
          />
        </Box>
      </Box>

      {/* CONTEÚDO — busca + tabela, mesmo padrão de DialogVenda.tsx */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: { xs: 0.75, sm: 1.25 }, p: { xs: 1, sm: 1.5, md: 2 }, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <OrdenarPorControl
            opcoes={tiposBuscaProduto}
            valorSelecionado={tipoBusca}
            label="Buscar por (F8)"
            minWidth={190}
            onChange={(novoTipo) => {
              setTipoBusca(novoTipo);
              setFiltro('');
              setSelectedIndex(0);
              setPage(0);
            }}
          />

          <ListagemBuscaField
            value={filtro}
            onChange={(valor) => {
              setFiltro(valor);
              setSelectedIndex(0);
              setPage(0);
            }}
            placeholder="Digite para buscar produto... (F8 alterna o campo de busca)"
            inputRef={buscaRef}
          />

          <OrdenarPorControl
            opcoes={modosDeBuscaProduto}
            valorSelecionado={modoBusca}
            label="Modo de busca"
            minWidth={190}
            onChange={(novoModo) => {
              setModoBusca(novoModo);
              setSelectedIndex(0);
              setPage(0);
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              borderRadius: '14px',
              overflow: 'hidden',
              border: `1px solid ${theme.torq.border.default}`,
              backgroundColor: theme.torq.surface.card,
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0
            }}
          >
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                scrollbarGutter: 'stable',
              }}
              ref={tabelaRef}
            >
              <Table size="small" stickyHeader sx={{ minWidth: '100%' }}>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        backgroundColor: theme.torq.surface.level2,
                        color: theme.torq.text.secondary,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        borderBottom: `1px solid ${theme.torq.border.default}`,
                      },
                    }}
                  >
                    <TableCell sx={{ width: '26%' }}>Produto</TableCell>
                    <TableCell sx={{ width: '8%' }}>Cód.</TableCell>
                    <TableCell sx={{ width: '5%' }}>Un</TableCell>
                    <TableCell sx={{ width: '8%' }}>Qtd. Disp.</TableCell>
                    <TableCell sx={{ width: '10%' }}>Marca</TableCell>
                    <TableCell sx={{ width: '10%' }}>Referência</TableCell>
                    <TableCell sx={{ width: '8%' }}>Tipo</TableCell>
                    <TableCell align='right' sx={{ width: '12.5%' }}>Preço Compra</TableCell>
                    <TableCell align='right' sx={{ width: '12.5%' }}>Preço Venda</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {produtos.map((prod, index) => {
                    const isFocused = index === selectedIndex;
                    const isSelected = idprodd === prod.idproduto;
                    const estoqueNegativo = (prod.quan ?? 0) < 0;
                    const estoqueZerado = (prod.quan ?? 0) === 0;

                    return (
                      <TableRow
                        key={prod.idproduto}
                        data-row-index={index}
                        onClick={() => selecionarProduto(prod, index)}
                        hover
                        tabIndex={0}
                        sx={{
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'background-color 0.15s ease',
                          backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                          borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                          '&:last-child td': { borderBottom: 0 },
                          '& td': { py: 0.75, px: 1, borderBottom: `1px solid ${theme.torq.border.default}` },
                          '&:hover': {
                            backgroundColor: isSelected
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.primary.main, 0.035),
                          },
                          ...(isFocused && {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: '-2px',
                          }),
                        }}
                      >
                        <TableCell>
                          <Box display='flex' alignItems='center' gap={1.25}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                flexShrink: 0,
                              }}
                            >
                              <Inventory2RoundedIcon sx={{ fontSize: '15px' }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                noWrap
                                sx={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.torq.text.primary }}
                              >
                                {prod.descricao}
                              </Typography>
                              {prod.infadd && (
                                <Typography noWrap sx={{ fontSize: '0.7rem', color: theme.torq.text.secondary }}>
                                  {prod.infadd}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.codinterno || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.un}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: '0.8125rem',
                              fontWeight: 600,
                              color: estoqueNegativo ? 'error.main' : estoqueZerado ? theme.torq.text.muted : PALETA.sucesso,
                            }}
                          >
                            {formatNumber(prod.quan ?? 0)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.marca || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.referencia || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={prod.original ? 'Própria' : 'Similar'}
                            size="small"
                            sx={{
                              height: '18px',
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 1 },
                              bgcolor: prod.original ? alpha(PALETA.sucesso, 0.12) : alpha(PALETA.atencao, 0.16),
                              color: prod.original ? PALETA.sucesso : PALETA.atencao,
                            }}
                          />
                        </TableCell>

                        <TableCell align='right'>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 700, color: theme.torq.text.primary }}>
                            {formatNumber(prod.preco, { style: 'currency', currency: 'BRL' })}
                          </Typography>
                        </TableCell>

                        <TableCell align='right'>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 700, color: PALETA.sucesso }}>
                            {formatNumber(prod.pvenda, { style: 'currency', currency: 'BRL' })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                {produtos.length === 0 && isLoading && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                          <CircularProgress size={26} thickness={4} />
                          <Typography variant='body2' color='text.secondary'>
                            Carregando produtos...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                {produtos.length === 0 && !isLoading && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        <Box>
                          <ImageIcon sx={{
                            fontSize: 32,
                            color: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.text.secondary, 0.4)
                              : 'text.secondary',
                            mb: 1
                          }} />
                          <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                            {Environment.LISTAGEM_VAZIA}
                          </Typography>
                          <Typography variant='body2' color='text.secondary' fontSize="0.75rem">
                            {filtro ? 'Tente ajustar os filtros de busca' : 'Nenhum produto encontrado'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                <TableFooter>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0 }}>
                        <LinearProgress sx={{ height: '1.5px' }} />
                      </TableCell>
                    </TableRow>
                  )}
                  {totalCount > rowsPerPage && (
                    <TableRow ref={footerRowRef}>
                      <TableCell colSpan={9} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                          <Pagination
                            page={page + 1}
                            count={Math.ceil(totalCount / rowsPerPage)}
                            onChange={(_, newPage) => setPage(newPage - 1)}
                            color="primary"
                            size="small"
                            shape="rounded"
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            boundaryCount={1}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Total de {totalCount} produto{totalCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableFooter>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>

      {/* PAINEL DO PRODUTO SELECIONADO — descrição + detalhes à esquerda, unidade/quantidade/
          preço/total (total sempre travado no valor original do item na nota) + botão à
          direita. Fora da área rolável, sempre visível. */}
      <Box sx={{ flexShrink: 0, px: { xs: 1, sm: 1.5, md: 2 }, pb: { xs: 1, sm: 1.5 } }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 2 },
            p: { xs: 1.25, sm: 1.5, md: 2 },
            borderRadius: '12px',
            border: `1px solid ${theme.torq.border.default}`,
            backgroundColor: theme.torq.surface.level2,
          }}
        >
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, minWidth: 0, flex: '1 1 280px' }}>
            <ArticleRoundedIcon sx={{ color: theme.palette.primary.main, fontSize: { xs: 22, sm: 28 }, mt: 0.25, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: theme.palette.primary.main, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Produto selecionado
              </Typography>
              <Typography noWrap sx={{ fontWeight: 700, color: theme.torq.text.primary }}>
                {idprodd ? descricao : 'Nenhum produto selecionado'}
              </Typography>
              {produtoEmFoco?.infadd && (
                <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {produtoEmFoco.infadd}
                </Typography>
              )}
              {idprodd && (
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: theme.palette.primary.main, textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: '0.68rem' }}>
                    Detalhes:{' '}
                  </Box>
                  Marca: {marca || '—'} • Referência: {referencia || '—'} • EAN da nota: {dados?.ean || '—'}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="UN *"
              value={un}
              onChange={handleUnidadeChange}
              error={!!erroUnidade}
              helperText={erroUnidade || undefined}
              sx={{ width: { xs: 80, sm: 90 }, backgroundColor: theme.torq.surface.card }}
            />

            <TextField
              size="small"
              label="Quantidade (F2)"
              inputRef={qtdInputRef}
              value={formatNumber(quantidade, { minimumFractionDigits: casasDecimais, maximumFractionDigits: casasDecimais })}
              onChange={handleQuantidadeChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  precoInputRef.current?.focus();
                  precoInputRef.current?.select();
                }
              }}
              inputProps={{ style: { textAlign: 'center' } }}
              sx={{ width: { xs: 100, sm: 120 }, backgroundColor: theme.torq.surface.card }}
            />

            <TextField
              size="small"
              inputRef={precoInputRef}
              label="Valor unitário"
              value={formatNumber(precoUnitario, { minimumFractionDigits: casasDecimais, maximumFractionDigits: casasDecimais })}
              onChange={handlePrecoChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  vincularProduto();
                }
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              sx={{ width: { xs: 120, sm: 140 }, backgroundColor: theme.torq.surface.card }}
            />

            <TextField
              size="small"
              label="Valor total"
              value={formatMoney(totalExibido)}
              disabled
              sx={{
                width: { xs: 120, sm: 150 },
                '& .MuiOutlinedInput-root.Mui-disabled': {
                  backgroundColor: alpha(PALETA.sucesso, 0.08),
                  '& fieldset': { borderColor: alpha(PALETA.sucesso, 0.4) },
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: PALETA.sucesso,
                  fontWeight: 700,
                },
              }}
            />

            <Button
              variant="outlined"
              color="primary"
              onClick={abrirEdicaoPreco}
              disabled={!idprodd}
              startIcon={<PriceChangeRoundedIcon />}
              sx={{
                height: { xs: 36, sm: 40 },
                px: { xs: 1.5, sm: 2 },
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
              }}
            >
              Editar Preço do Produto
            </Button>

            <Button
              ref={btnVincularRef}
              variant="contained"
              color="primary"
              onClick={vincularProduto}
              disabled={!idprodd || quantidade <= 0 || precoUnitario <= 0}
              startIcon={<LinkRoundedIcon />}
              sx={{
                height: { xs: 36, sm: 40 },
                px: { xs: 1.5, sm: 2.5 },
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  vincularProduto();
                }
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
            >
              Vincular (ENTER)
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Atualiza o CADASTRO do produto selecionado na tabela (preço de compra, percentuais
          de custo, preço de custo calculado, margem e preço de venda) — mesmos campos/fórmulas
          da aba "Formando Preço" do cadastro de produto (ver DialogCadastrarProd.tsx). Não
          mexe em nada do vínculo/valores deste item da NF-e. */}
      <Dialog open={precoFormOpen} onClose={fecharEdicaoPreco} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PriceChangeRoundedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Editar Preço do Produto</Typography>
            <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {descricao || produtoCompleto?.descricao || ''}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {precoFormLoading && !produtoCompleto ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} thickness={4} />
            </Box>
          ) : (
            <>
              {/* Alterna o Preço De Compra entre o valor já cadastrado hoje no produto e o
                  valor unitário desta compra/NF-e — o resto (custo/venda/promoção) recalcula
                  em cima da troca. */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  clickable
                  label={`Preço atual do produto: ${formatNumber(precoAtualProduto, { style: 'currency', currency: 'BRL' })}`}
                  color={origemPreco === 'atual' ? 'primary' : 'default'}
                  variant={origemPreco === 'atual' ? 'filled' : 'outlined'}
                  onClick={() => alternarOrigemPreco('atual')}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  clickable
                  label={`Preço desta compra: ${formatNumber(precoNovaCompra, { style: 'currency', currency: 'BRL' })}`}
                  color={origemPreco === 'novaCompra' ? 'primary' : 'default'}
                  variant={origemPreco === 'novaCompra' ? 'filled' : 'outlined'}
                  onClick={() => alternarOrigemPreco('novaCompra')}
                  disabled={precoNovaCompra <= 0}
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              <Grid container spacing={1.5}>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Preço De Compra"
                  value={formatNumber(precoForm.preco, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handlePrecoFormCampoChange('preco')}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Comissão (%)"
                  value={formatNumber(precoForm.comissao, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handlePrecoFormCampoChange('comissao')}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="ICMS (%)"
                  value={formatNumber(precoForm.icms, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handlePrecoFormCampoChange('icms')}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Frete (%)"
                  value={formatNumber(precoForm.pfrete, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handlePrecoFormCampoChange('pfrete')}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Seguro"
                  value={formatNumber(precoForm.pseguro, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handlePrecoFormCampoChange('pseguro')}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Desconto"
                  value={formatNumber(precoForm.desconto, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handleDescontoFormChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Preço promoção"
                  value={formatNumber(precoForm.ppromocao, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handlePromocaoFormChange}
                  helperText="Não pode ficar abaixo do preço de custo"
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Preço De Custo"
                  disabled
                  value={formatNumber(precoForm.precocusto, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mar. Lucro (%)"
                  value={formatNumber(precoForm.margem, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handleMargemFormChange}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Preço Venda"
                  value={formatNumber(precoForm.pvenda, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  onChange={handlePrecoVendaFormChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    sx: { fontWeight: 700, color: PALETA.sucesso },
                  }}
                />
              </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={fecharEdicaoPreco} disabled={precoFormLoading} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvarPrecoProduto}
            disabled={precoFormLoading || !produtoCompleto}
            startIcon={precoFormLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

    </Dialog>
  );
};
